"use client";

import * as React from "react";
import * as SliderPrimitive from "@radix-ui/react-slider";

import { cn } from "./utils";

function Slider({
  className,
  defaultValue,
  value,
  min = 0,
  max = 100,
  variant = "default",
  ...props
}: React.ComponentProps<typeof SliderPrimitive.Root> & {
  /**
   * "gradient" is the opt-in look approved for the chart-history-window
   * picker: a track that always shows the full green -> amber -> orange ->
   * red gradient (never a growing/shrinking fill) with a larger,
   * white-ringed, glowing thumb. Every other consumer keeps the plain
   * teal-fill "default" look untouched.
   */
  variant?: "default" | "gradient";
}) {
  const _values = React.useMemo(
    () =>
      Array.isArray(value)
        ? value
        : Array.isArray(defaultValue)
          ? defaultValue
          : [min, max],
    [value, defaultValue, min, max],
  );
  const isGradient = variant === "gradient";

  return (
    <SliderPrimitive.Root
      data-slot="slider"
      defaultValue={defaultValue}
      value={value}
      min={min}
      max={max}
      className={cn(
        "relative flex w-full touch-none items-center select-none data-disabled:opacity-50 data-[orientation=vertical]:h-full data-[orientation=vertical]:min-h-44 data-[orientation=vertical]:w-auto data-[orientation=vertical]:flex-col",
        className,
      )}
      {...props}
    >
      <SliderPrimitive.Track
        data-slot="slider-track"
        style={
          isGradient
            ? {
                backgroundImage:
                  "linear-gradient(to right, #22c55e 0%, #eab308 38%, #f97316 66%, #ef4444 100%)",
              }
            : undefined
        }
        className={cn(
          "bg-surface-sunken relative grow overflow-hidden rounded-full data-[orientation=horizontal]:h-4 data-[orientation=horizontal]:w-full data-[orientation=vertical]:h-full data-[orientation=vertical]:w-1.5",
          isGradient && "data-[orientation=horizontal]:h-[7px]",
        )}
      >
        <SliderPrimitive.Range
          data-slot="slider-range"
          // The gradient track is always shown in full — it is NOT a fill
          // that grows with the value — so the Range overlay (which Radix
          // draws from the track start to the current value) is made fully
          // transparent here instead of tinting over the gradient.
          style={isGradient ? { backgroundColor: "transparent" } : undefined}
          className={cn(
            "bg-primary absolute data-[orientation=horizontal]:h-full data-[orientation=vertical]:w-full",
          )}
        />
      </SliderPrimitive.Track>
      {Array.from({ length: _values.length }, (_, index) => (
        <SliderPrimitive.Thumb
          data-slot="slider-thumb"
          key={index}
          style={
            isGradient
              ? { boxShadow: "0 0 0 3px rgba(255,255,255,0.9), 0 0 10px 4px rgba(34,197,94,0.45)" }
              : undefined
          }
          className={cn(
            "border-primary bg-background ring-ring/50 block size-4 shrink-0 rounded-full border shadow-sm transition-[color,box-shadow] hover:ring-4 focus-visible:ring-4 focus-visible:outline-hidden disabled:pointer-events-none disabled:opacity-50",
            isGradient && "size-5 border-2 border-white bg-background hover:ring-0 focus-visible:ring-0",
          )}
        />
      ))}
    </SliderPrimitive.Root>
  );
}

export { Slider };
